import React from "react"
import { withPrefix } from "gatsby"
import Layout from "../../components/layout"
import Video from "../../components/video"
import { AppendixWaypoint, Appendix } from "../../components/appendix"
import Hero from "../../components/hero"
import MegaQuote from "../../components/mega-quote"
import { PageColumns, Column } from "../../components/page-column"
import { StatContainer } from "../../components/stat"
import ReadNext from "../../components/read-next"
import MotionIcon from "../../components/motion-icon"
import IconGlobeThermometer from "../../images/spotlights/toward-a-green-inclusive-digital-future/icon-globe-thermometer.inline.svg"
import StaticChart from "../../components/static-chart"
import ChartRedistribution from "../../images/spotlights/toward-a-green-inclusive-digital-future/redistribution.inline.svg"
import MobileOnly from "../../components/mobile-only"

const PAGESLUG = "toward-a-green-inclusive-digital-future"

const figure1_5 = (
  <StaticChart
    figureNumber="1.5"
    title="Policies to Tackle Rising Inequality"
    subTitle="Predistributive policies reduce market income inequality (before taxes and transfers), while redistributive policies reduce poverty and disposable income inequality (after taxes and transfers)."
    ariaLabel="Countries can use both predistribution and redistribution to reduce inequality. Predistribution involves the provision of better services and the support of the free market. Redistribution involves moving income between citizens with taxes and transfers."
    paddingBottom={10}
    scaling={true}
  >
    <ChartRedistribution />
  </StaticChart>
)

const stat = (
  <StatContainer>
    <MotionIcon icon="globe-thermometer">
      <IconGlobeThermometer />
    </MotionIcon>
    <div className="stat__text">
      <p>
        The IMF, in collaboration with other international organizations,
        country authorities, and private data providers, recently launched a
        climate change indicators dashboard, leveraging the IMF’s leadership in
        statistical methodology.
      </p>
      <p>
        <a href="https://climatedata.imf.org/">
          Go to Climate Change Indicators Dashboard
        </a>
      </p>
    </div>
  </StatContainer>
)

const video = (
  <Video
    videoNative={withPrefix("/") + "video/toward-a-green-inclusive-digital-future/toward-a-green-inclusive-digital-future.mp4"}
    track={withPrefix("/") + "video/toward-a-green-inclusive-digital-future/toward-a-green-inclusive-digital-future.vtt"}
    posterImage={
      "spotlights/toward-a-green-inclusive-digital-future/video-poster.jpg"
    }
  />
)

export default function TowardAGreenInclusiveDigitalFuture() {
  return (
    <Layout>
      <section className="page page--margin-large">
        <Hero
          title="Toward a Green, Inclusive, and Digital Future"
          text="Governments need to take action to strengthen the resilience of people and the planet."
          image="spotlights/toward-a-green-inclusive-digital-future/hero.jpg"
          video={
            withPrefix("/") +
            "video/toward-a-green-inclusive-digital-future/hero.mp4"
          }
        />

        <PageColumns>
          <Column type="text">
            <p className="dropcap page__intro">
              As vaccination campaigns advance and economies move forward on the
              path to recovery, investments in green infrastructure, social
              assistance programs, and digitalization will be essential to
              fostering a more equitable and sustainable recovery. The world has
              an opportunity to build forward better and channel resources into
              a new economy—one that is greener, invests in human capital, and
              strengthens the resilience of businesses and communities.
            </p>

            <h2>Inclusive growth</h2>
            <AppendixWaypoint page={PAGESLUG} id={1} />
            <p>
              COVID-19 is widening inequality worldwide. To give everyone a fair
              shot at prosperity, governments need to improve access to basic
              public services and strengthen redistributive policies (see Figure
              1.5). These reforms must be complemented by greater transparency
              and accountability.
            </p>
            <p>
              The IMF continues to offer policy advice on strengthening social
              protection systems and fiscal positions through revenue
              mobilization—including options for broadening the tax base and
              more progressive taxation—and on tackling tax avoidance and
              illicit financial flows. Analytical work on gender equity and on
              income and wealth inequality is also underway, as is work on
              inclusive financial services.
            </p>
            <MobileOnly>{figure1_5}</MobileOnly>
          </Column>

          <Column type="media">{figure1_5}</Column>
        </PageColumns>

        {/* Greening the recovery */}
        <PageColumns>
          <Column type="media">{stat}</Column>

          <Column type="text">
            <h2>Greening the recovery</h2>
            <p>
              Policymakers across the globe are rightly focused on fighting the
              COVID-19 crisis. But the climate change crisis remains, as does
              the need for decisive policy action to address it. Indeed, current
              policy decisions to facilitate recovery from the crisis may shape
              the world’s climate for decades. This calls for fiscal
              policymakers to “green” their response to the crisis.
            </p>
            <p>
              The IMF has rapidly scaled up its work on climate. Climate-related
              issues and policies are being more systematically integrated into
              surveillance, and various policy papers and books have been
              published on energy subsidies, carbon pricing, natural disaster
              clauses in state-contingent debt instruments, and the impact of
              climate change on macroeconomic and financial stability.
              Climate-related risks are also being integrated into stress
              testing and financial stability monitoring as part of the
              IMF–World Bank Financial Sector Assessment Program (FSAP).
              Furthermore, work is ongoing to integrate climate considerations
              into the public financial management cycle (“green budgeting”) and
              into infrastructure governance.
            </p>
            <MobileOnly>{stat}</MobileOnly>
            <p>
              To help close data gaps, the IMF is supporting efforts to spread
              further the adoption of climate disclosure across markets. In
              collaboration with other international organizations, country
              authorities, and private data providers, the IMF also recently
              launched a Climate Change Indicators Dashboard, leveraging the
              IMF’s leadership in statistical methodology. The dashboard
              delivers a standardized set of comparable cross-country data and
              makes climate change indicators available sooner and with greater
              frequency.
            </p>
          </Column>
        </PageColumns>

        {/* Digitization */}
        <PageColumns modifiers="md:pull--large">
          <Column type="text">
            <h2>Digitalization</h2>
            <p>
              The COVID-19 crisis is accelerating the move toward digitalization
              and the use of digital money. This trend will likely reshape the
              international monetary system. Whether the system will ultimately
              be safer and more efficient depends on how well IMF member
              countries coordinate in seizing opportunities and managing risks.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={2} />
            <p>
              Building on the Bali Fintech Agenda, the IMF has ramped up its
              work on the macro-financial implications of central bank digital
              currencies and privately issued digital money, with a focus on the
              benefits and risks, the regulation and supervision of stablecoin
              issuers and service providers, cross-border payments, the role of
              digital money in fostering financial inclusion, and its treatment
              in macroeconomic statistics. The use of personal data in the
              digital economy and the need for global policy coordination are
              also being explored.
            </p>
            <MobileOnly>{video}</MobileOnly>
            <p>
              The IMF will continue to analyze and deepen coverage of these
              issues in its surveillance, including under the FSAP, and will
              focus its CD on helping countries leverage opportunities from
              digitalization while managing cyber risks and risks to financial
              integrity. To improve the administration of tax and customs
              operations, the IMF is also offering advice on and support for
              digital government services, including information technology (IT)
              strategies, financial management information systems,
              government-to-person cash transfers, and digital taxpayer
              services.
            </p>
          </Column>

          <Column type="media">{video}</Column>
        </PageColumns>
      </section>

      <div className="container container--max">
        <MegaQuote
          quote="“Together we can accelerate this historic transition to a greener and more resilient economy.”"
          name="Kristalina Georgieva"
          jobTitle="Managing Director"
          img="spotlights/toward-a-green-inclusive-digital-future/mega-quote-bg.jpg"
        />
      </div>

      <ReadNext sectionID="what-we-do" pageID="economic-surveillance" />
      <Appendix page={PAGESLUG} />
    </Layout>
  )
}
